// Places the search box suggests, searched in the browser (owner's approval,
// 2026-09-24: search, variant B).
//
// The list is every Canadian municipality of 1,000 people or more (Statistics
// Canada, 2021 Census; scripts/build-places.mjs), about 1,900 names, 20 KB
// compressed, loaded the first time the search box is used. Searching it takes
// well under a millisecond, so places appear as the visitor types, without a
// round trip, with accents ignored, a typo forgiven and the bigger place first.

import { PROVINCES, foldPlace } from '@/lib/provinces';

export type PlaceRow = [name: string, province: string, population: number, frenchName?: string];

export type Place = {
  kind: 'city' | 'province';
  name: string;
  /** The French form, when the place has one of its own. */
  frenchName?: string;
  /** Two-letter code; for a province, its own. */
  province: string;
  population: number;
};

type Indexed = Place & { keys: string[]; words: string[] };

export type PlaceIndex = Indexed[];

const index = (place: Place): Indexed => {
  const keys = [place.name, place.frenchName].filter(Boolean).map((n) => foldPlace(n!));
  return { ...place, keys, words: keys.flatMap((k) => k.split(/[ /]+/)) };
};

export function buildPlaceIndex(rows: PlaceRow[]): PlaceIndex {
  const provinces: Place[] = PROVINCES.map((p) => ({
    kind: 'province',
    name: p.en,
    frenchName: p.fr !== p.en ? p.fr : undefined,
    province: p.code,
    // Above every city, so "Ontario" lists the province before "Ontario, OR".
    population: Number.MAX_SAFE_INTEGER,
  }));
  const cities: Place[] = rows.map(([name, province, population, frenchName]) => ({ kind: 'city', name, frenchName, province, population }));
  return [...provinces, ...cities].map(index);
}

/** Edit distance, but stops counting past `max`. */
export function withinEdits(a: string, b: string, max: number): boolean {
  if (Math.abs(a.length - b.length) > max) return false;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      best = Math.min(best, cur[j]);
    }
    if (best > max) return false;
    prev = cur;
  }
  return prev[b.length] <= max;
}

// How well a place answers what was typed: the whole name, the start of it,
// the start of one of its words, or - from four letters - the start of it with
// a typo (one from four letters, two from eight).
function score(place: Indexed, q: string): number {
  if (place.keys.some((k) => k === q)) return 4;
  if (place.keys.some((k) => k.startsWith(q))) return 3;
  if (place.words.some((w) => w.startsWith(q))) return 2;
  if (q.length >= 4) {
    const max = q.length >= 8 ? 2 : 1;
    if (place.keys.some((k) => withinEdits(q, k.slice(0, q.length), max) || withinEdits(q, k, max))) return 1;
  }
  return 0;
}

// "St-Lambert", "Ste-Julie", "Mt-Tremblant": the short forms people type.
const SHORT_FORMS: Record<string, string> = { st: 'saint', ste: 'sainte', mt: 'mont' };
export const expandShortForms = (folded: string) => folded.split(' ').map((w) => SHORT_FORMS[w] ?? w).join(' ');

export function searchPlaces(places: PlaceIndex, text: string, limit = 5): Place[] {
  const q = expandShortForms(foldPlace(text || ''));
  if (q.length < 2) return [];
  const hits: { place: Indexed; score: number }[] = [];
  for (const place of places) {
    const s = score(place, q);
    if (s > 0) hits.push({ place, score: s });
  }
  hits.sort((a, b) => b.score - a.score || b.place.population - a.place.population);
  return hits.slice(0, limit).map(({ place }) => {
    const { ...plain } = place;
    return plain;
  });
}

/**
 * The place a word or phrase names exactly - "montreal" is Montréal, QC -
 * preferring the given province, then the bigger place. Cities only.
 */
export function resolvePlace(places: PlaceIndex, text: string, province?: string): Place | undefined {
  const q = expandShortForms(foldPlace(text || ''));
  if (!q) return undefined;
  let best: Indexed | undefined;
  for (const place of places) {
    if (place.kind !== 'city' || !place.keys.includes(q)) continue;
    if (province && place.province !== province) continue;
    if (!best || place.population > best.population) best = place;
  }
  if (!best) return undefined;
  const { ...plain } = best;
  return plain;
}

/** The name to show in the visitor's language. */
export const placeName = (place: Pick<Place, 'name' | 'frenchName'>, lang: string) =>
  lang === 'fr' && place.frenchName ? place.frenchName : place.name;

let loading: Promise<PlaceIndex> | null = null;

/** The list, fetched once, in its own chunk, the first time it is asked for. */
export function loadPlaceIndex(): Promise<PlaceIndex> {
  if (!loading) {
    loading = import('@/lib/places/canada-places.json').then((m) => buildPlaceIndex((m.default ?? m) as unknown as PlaceRow[]));
  }
  return loading;
}

/** One entry in the search box's list of places. */
export type PlaceSuggestion = {
  kind: 'city' | 'province';
  /** The name the results filter by; none for a province. */
  city: string | null;
  province: string;
  label: string;
  /** Listings there; null while not yet counted. */
  listings: number | null;
};

export const placeKey = (city: string, province: string) => `${expandShortForms(foldPlace(city))}|${province}`;

/**
 * The places to show: what the browser found, each with the number of
 * listings the server counted (a place the server was asked about and did not
 * return has none yet), plus cities with listings the national list does not
 * name (a smaller place, another spelling). Provinces stay on top; then places
 * with listings, so the first choices lead somewhere.
 */
export function mergePlaceSuggestions(
  local: Place[],
  askedKeys: ReadonlySet<string>,
  counts: { city: string; province: string; listings: number }[],
  withListings: { city: string; province: string; listings: number }[],
  lang: string,
  limit = 6
): PlaceSuggestion[] {
  const counted = new Map<string, number>();
  for (const c of withListings) counted.set(placeKey(c.city, c.province), c.listings);
  for (const c of counts) counted.set(placeKey(c.city, c.province), c.listings);

  const provinces: PlaceSuggestion[] = [];
  const cities: PlaceSuggestion[] = [];
  const seen = new Set<string>();
  for (const p of local) {
    if (p.kind === 'province') {
      provinces.push({ kind: 'province', city: null, province: p.province, label: placeName(p, lang), listings: null });
      continue;
    }
    const key = placeKey(p.name, p.province);
    seen.add(key);
    const listings = counted.get(key) ?? (askedKeys.has(key) ? 0 : null);
    cities.push({ kind: 'city', city: p.name, province: p.province, label: `${placeName(p, lang)}, ${p.province}`, listings });
  }
  for (const c of withListings) {
    const key = placeKey(c.city, c.province);
    if (seen.has(key)) continue;
    seen.add(key);
    cities.push({ kind: 'city', city: c.city, province: c.province, label: `${c.city}, ${c.province}`, listings: c.listings });
  }
  // Stable: within each half the national list's order (match, then size) holds.
  const ranked = [...cities.filter((c) => (c.listings ?? 0) > 0), ...cities.filter((c) => !((c.listings ?? 0) > 0))];
  return [...provinces, ...ranked].slice(0, limit);
}

/**
 * A place named inside a longer phrase: "cozy Laval" is Laval with "cozy" left
 * over for the text search, so an unknown word never turns the whole phrase
 * into a city that does not exist. The longest run of words that names a city
 * wins; a lone word must be a whole city name, not a part of one.
 */
const MAX_WORDS_IN_PLACE_PHRASE = 8;

export function resolvePlaceIn(places: PlaceIndex, text: string, province?: string): { place: Place; rest: string } | undefined {
  const words = (text || '').trim().split(/\s+/).filter(Boolean);
  // Every run of words is tried: a long phrase is no place name, and would cost
  // a pass over the whole list for each run.
  if (words.length > MAX_WORDS_IN_PLACE_PHRASE) return undefined;
  for (let len = words.length; len >= 1; len--) {
    for (let start = 0; start + len <= words.length; start++) {
      const place = resolvePlace(places, words.slice(start, start + len).join(' '), province);
      if (place) return { place, rest: [...words.slice(0, start), ...words.slice(start + len)].join(' ') };
    }
  }
  // Nothing named exactly: a typo. Only a word of five letters or more, and
  // only one edit away (two from eight letters), so a plain word is not bent
  // into a city; the bigger city wins a tie.
  for (let len = words.length; len >= 1; len--) {
    for (let start = 0; start + len <= words.length; start++) {
      const q = expandShortForms(foldPlace(words.slice(start, start + len).join(' ')));
      if (q.length < 5) continue;
      const max = q.length >= 8 ? 2 : 1;
      let best: Indexed | undefined;
      for (const place of places) {
        if (place.kind !== 'city' || (province && place.province !== province)) continue;
        if (!place.keys.some((k) => withinEdits(q, k, max))) continue;
        if (!best || place.population > best.population) best = place;
      }
      if (best) {
        const { ...plain } = best;
        return { place: plain, rest: [...words.slice(0, start), ...words.slice(start + len)].join(' ') };
      }
    }
  }
  return undefined;
}
